'use client';
import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PlpFilterGroup } from './plp-filter-sidebar';
import { usePlpFilters } from './plp-use-filters';

// Mobile-only (< lg) filter entry point: a sideways-scrolling row of short filter pills, one per
// group. Tapping one opens a bottom sheet with its options. Sits between the chips and product grid.
const PlpMobileFilters = ({ filters }: { filters: PlpFilterGroup[] }) => {
  const [active, setActive] = useState<PlpFilterGroup | null>(null);
  const { isChecked, toggle } = usePlpFilters();

  return (
    <div className="mt-24 font-gt text-[#212121] lg:hidden">
      <div className="flex items-center gap-12">
        {/* Fixed label — stays put while the pills scroll */}
        <span className="shrink-0 text-14 font-medium">Filters:</span>
        <div className="flex min-w-0 flex-1 gap-8 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {filters.map((group) => (
            <button
              key={group.label}
              type="button"
              onClick={() => setActive(group)}
              className="flex h-44 shrink-0 items-center whitespace-nowrap border border-[#d7d7d7] px-16 text-14 font-medium transition-colors hover:bg-[#f0f0f0]"
            >
              {group.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bottom sheet for the tapped filter */}
      {active && (
        <>
          <div className="fixed inset-0 z-[70] bg-black/30" onClick={() => setActive(null)} aria-hidden />
          <div className="fixed inset-x-0 bottom-0 z-[70] flex max-h-[70vh] flex-col bg-white">
            <div className="flex items-center justify-between border-b border-[#d7d7d7] px-24 py-16">
              <span className="text-16 font-medium">{active.label}</span>
              <button type="button" aria-label="Close" onClick={() => setActive(null)} className="transition-opacity hover:opacity-70">
                <XMarkIcon className="size-24" />
              </button>
            </div>

            <ul className="flex flex-col gap-16 overflow-y-auto px-24 py-20">
              {(active.options ?? []).map((option) => (
                <li key={option.value}>
                  <label className="flex min-h-44 cursor-pointer items-center gap-12 text-16">
                    <input
                      type="checkbox"
                      checked={isChecked(active.identifier, option.value)}
                      onChange={() => toggle(active.identifier, option.value)}
                      className="size-16 shrink-0 cursor-pointer appearance-none border border-[#9e9e9e] checked:border-[#1c1c1c] checked:bg-[#1c1c1c]"
                    />
                    {option.label}
                    {typeof option.count === 'number' && <span className="text-[#9e9e9e]">({option.count})</span>}
                  </label>
                </li>
              ))}
            </ul>

            <div className="border-t border-[#d7d7d7] px-24 py-16">
              <button
                type="button"
                onClick={() => setActive(null)}
                className="flex h-44 w-full items-center justify-center bg-[#1c1c1c] text-14 text-white transition-opacity hover:opacity-90"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PlpMobileFilters;
